import React, { useState, useMemo, useEffect } from 'react';
import { X, TrendingUp, Users, Calendar, MapPin, Filter, Search, ChevronDown, ChevronUp, Zap, Phone, Mail, Loader } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { sourcesService } from '../../services/api';
import { formatDate } from '../../utils/helpers';
import { Dropdown, DropdownTrigger, DropdownPopover, DropdownMenu, DropdownItem } from '../ui/Dropdown';

const formatCurrency = (amount) => {
  if (!amount) return 'Rs 0';
  if (amount >= 1000000) return `Rs ${(amount / 1000000).toFixed(1)}M`;
  if (amount >= 1000) return `Rs ${(amount / 1000).toFixed(0)}k`;
  return `Rs ${amount}`;
};

const formatPercent = (value) => {
  if (!value || !isFinite(value)) return '0%';
  return `${value.toFixed(1)}%`;
};

export default function SourceDetailModal({ source, onClose, onSelectLead }) {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState('campaigns');
  const [typeFilter, setTypeFilter] = useState('Prequalification');
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedRows, setExpandedRows] = useState(new Set());

  // Pagination states
  const [visibleCampaigns, setVisibleCampaigns] = useState(50);
  const [visibleLocations, setVisibleLocations] = useState(50);
  const [visibleLeads, setVisibleLeads] = useState(50);

  // Fetch prequalifications from server for this specific source
  const [sourceLeads, setSourceLeads] = useState([]);
  const [overallStats, setOverallStats] = useState({ total: 0, booked: 0, totalRevenue: 0, conversionRate: 0, avgDealSize: 0 });
  const [loadingLeads, setLoadingLeads] = useState(true);

  useEffect(() => {
    const fetchSourceLeads = async () => {
      setLoadingLeads(true);
      try {
        const response = await sourcesService.getSourceLeads(source.id);
        const transformedPq = (response.data || []).map(item => ({
          ...item,
          itemType: 'Prequalification',
          clientName: item.profile?.clientName || item.profile?.contactPersonName || item.contactPerson || item.title || 'Unknown',
          stage: item.status,
          amount: item.earnestMoneyValue || item.clientBudget || item.finalAmount || item.quotationAmount || 0,
          phone: item.profile?.mobile || '',
          email: item.profile?.email || '',
          source: item.source?.name || (typeof item.source === 'string' ? item.source : '') || source?.name || '',
        }));

        const transformedTenders = (response.tenders || []).map(item => ({
          ...item,
          itemType: 'Tender/Proposal',
          clientName: item.profile?.clientName || item.profile?.contactPersonName || item.contactPerson || item.title || 'Unknown',
          stage: item.status,
          amount: item.proposalValue || item.grossProfit || 0,
          phone: item.profile?.mobile || '',
          email: item.profile?.email || '',
          source: item.source?.name || (typeof item.source === 'string' ? item.source : '') || source?.name || '',
        }));

        const combined = [...transformedPq, ...transformedTenders].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setSourceLeads(combined);
        setOverallStats(response.stats || { total: 0, booked: 0, totalRevenue: 0, conversionRate: 0, avgDealSize: 0 });
      } catch (err) {
        console.error('Failed to fetch source prequalifications:', err);
      } finally {
        setLoadingLeads(false);
      }
    };

    fetchSourceLeads();
  }, [source.id]);

  // Campaign breakdown (for Meta prequalifications)
  const campaignStats = useMemo(() => {
    const campaigns = {};

    sourceLeads.forEach(prequalification => {
      const campaignName = prequalification.campaignName || 'Unknown Campaign';

      if (!campaigns[campaignName]) {
        campaigns[campaignName] = {
          name: campaignName,
          prequalifications: [],
          booked: 0,
          totalRevenue: 0,
          platforms: new Set(),
          adSets: new Set(),
          ads: new Set()
        };
      }

      campaigns[campaignName].prequalifications.push(prequalification);
      if (['Booked', 'Won', 'Completed'].includes(prequalification.stage) || ['Booked', 'Won', 'Completed'].includes(prequalification.status)) {
        campaigns[campaignName].booked++;
        campaigns[campaignName].totalRevenue += (prequalification.finalAmount || prequalification.amount || 0);
      }
      if (prequalification.platform) campaigns[campaignName].platforms.add(prequalification.platform);
      if (prequalification.adsetName) campaigns[campaignName].adSets.add(prequalification.adsetName);
      if (prequalification.adName) campaigns[campaignName].ads.add(prequalification.adName);
    });

    return Object.values(campaigns)
      .map(c => ({
        ...c,
        count: c.prequalifications.length,
        conversionRate: c.prequalifications.length > 0 ? (c.booked / c.prequalifications.length) * 100 : 0,
        avgDealSize: c.booked > 0 ? c.totalRevenue / c.booked : 0,
        platforms: Array.from(c.platforms),
        adSets: Array.from(c.adSets),
        ads: Array.from(c.ads)
      }))
      .sort((a, b) => b.count - a.count);
  }, [sourceLeads]);

  // Location breakdown
  const locationStats = useMemo(() => {
    const locations = {};

    sourceLeads.forEach(prequalification => {
      const city = prequalification.clientAddress || 'Unknown';

      if (!locations[city]) {
        locations[city] = { name: city, prequalifications: [], booked: 0, totalRevenue: 0 };
      }

      locations[city].prequalifications.push(prequalification);
      if (['Booked', 'Won', 'Completed'].includes(prequalification.stage) || ['Booked', 'Won', 'Completed'].includes(prequalification.status)) {
        locations[city].booked++;
        locations[city].totalRevenue += (prequalification.finalAmount || prequalification.amount || 0);
      }
    });

    return Object.values(locations)
      .map(l => ({
        ...l,
        count: l.prequalifications.length,
        conversionRate: l.prequalifications.length > 0 ? (l.booked / l.prequalifications.length) * 100 : 0
      }))
      .sort((a, b) => b.count - a.count);
  }, [sourceLeads]);

  // Filter prequalifications based on selected campaign and search
  const filteredLeads = useMemo(() => {
    let filtered = sourceLeads;

    if (typeFilter !== 'All') {
      filtered = filtered.filter(l => l.itemType === typeFilter);
    }

    if (selectedCampaign) {
      filtered = filtered.filter(l =>
        (l.campaignName || 'Unknown Campaign') === selectedCampaign
      );
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(l =>
        l.clientName?.toLowerCase().includes(query) ||
        l.phone?.includes(query) ||
        l.email?.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [sourceLeads, selectedCampaign, searchQuery, typeFilter]);

  const toggleRowExpand = (campaignName) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(campaignName)) {
        next.delete(campaignName);
      } else {
        next.add(campaignName);
      }
      return next;
    });
  };

  const isMetaSource = source.name === 'Meta Prequalification Gen';

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white w-full max-w-5xl h-[90vh] rounded-2xl shadow-2xl flex flex-col overflow-hidden animate-scale-in" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6 flex justify-between items-center">
          <div className="flex items-center gap-4">
            {source.isIntegration && (
              <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                <Zap className="w-6 h-6" />
              </div>
            )}
            <div>
              <h2 className="text-2xl font-bold">{source.name}</h2>
              <p className="text-blue-100 text-sm mt-1">
                {overallStats.totalPq || 0} Prequalifications • {overallStats.totalTenders || 0} Tenders
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-full transition-colors">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Stats Bar */}
        <div className="p-4 bg-slate-50 border-b">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            
            {/* Prequalifications Box */}
            <div className="bg-white p-4 rounded-xl border border-blue-100 shadow-sm relative overflow-hidden">
              <div className="absolute top-0 left-0 w-1 h-full bg-blue-500"></div>
              <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wider mb-4 flex items-center gap-2">
                <Users className="w-4 h-4 text-blue-600" /> Prequalifications
              </h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <div className="text-xs text-gray-500 uppercase font-semibold mb-1">Total</div>
                  <div className="text-xl font-bold text-gray-900">{overallStats.totalPq || 0}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 uppercase font-semibold mb-1">Enlisted</div>
                  <div className="text-xl font-bold text-gray-900">{overallStats.enlistedPq || 0}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 uppercase font-semibold mb-1">Conv %</div>
                  <div className={`text-xl font-bold ${overallStats.pqConversionRate >= 20 ? 'text-green-600' : overallStats.pqConversionRate > 0 ? 'text-amber-600' : 'text-gray-900'}`}>
                    {(overallStats.pqConversionRate || 0).toFixed(1)}%
                  </div>
                </div>
              </div>
            </div>

            {/* Tenders Box */}
            <div className="bg-white p-4 rounded-xl border border-emerald-100 shadow-sm relative overflow-hidden">
              <div className="absolute top-0 left-0 w-1 h-full bg-emerald-500"></div>
              <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wider mb-4 flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-emerald-600" /> Tenders & Proposals
              </h3>
              <div className="grid grid-cols-4 gap-4">
                <div>
                  <div className="text-xs text-gray-500 uppercase font-semibold mb-1">Total</div>
                  <div className="text-xl font-bold text-gray-900">{overallStats.totalTenders || 0}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 uppercase font-semibold mb-1">Awarded</div>
                  <div className="text-xl font-bold text-gray-900">{overallStats.awardedTendersCount || 0}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 uppercase font-semibold mb-1">Conv %</div>
                  <div className={`text-xl font-bold ${overallStats.tenderConversionRate >= 20 ? 'text-green-600' : overallStats.tenderConversionRate > 0 ? 'text-amber-600' : 'text-gray-900'}`}>
                    {(overallStats.tenderConversionRate || 0).toFixed(1)}%
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 uppercase font-semibold mb-1">Revenue</div>
                  <div className="text-lg font-bold text-gray-900 truncate" title={formatCurrency(overallStats.totalRevenue)}>
                    {formatCurrency(overallStats.totalRevenue || 0)}
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>

        {/* Tabs - Only show for Meta source */}
        {isMetaSource && (
          <div className="flex gap-2 px-4 pt-4 bg-white border-b">
            <button
              onClick={() => setActiveTab('campaigns')}
              className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-colors ${activeTab === 'campaigns'
                ? 'bg-blue-50 text-blue-700 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700'
                }`}
            >
              {t('campaign')}
            </button>
            <button
              onClick={() => setActiveTab('locations')}
              className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-colors ${activeTab === 'locations'
                ? 'bg-blue-50 text-blue-700 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700'
                }`}
            >
              Locations
            </button>
            <button
              onClick={() => setActiveTab('prequalifications')}
              className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-colors ${activeTab === 'prequalifications'
                ? 'bg-blue-50 text-blue-700 border-b-2 border-blue-600'
                : 'text-gray-500 hover:text-gray-700'
                }`}
            >
              All Prequalifications & Tenders
            </button>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {loadingLeads ? (
            <div className="flex flex-col items-center justify-center py-16 text-gray-400">
              <Loader className="w-8 h-8 animate-spin mb-3" />
              <p className="text-sm">Loading records...</p>
            </div>
          ) : (
          <>
          {/* Campaign Breakdown Tab */}
          {(activeTab === 'campaigns' && isMetaSource) && (
            <div className="space-y-3">
              <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-3">
                Campaign Performance
              </h3>

              {campaignStats.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <Filter className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No campaign data available</p>
                  <p className="text-xs mt-1">Prequalifications imported before the update won&apos;t have campaign info</p>
                </div>
              ) : (
                <div className="bg-white rounded-xl border overflow-hidden">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-slate-50 text-left text-xs font-semibold text-gray-500 uppercase">
                        <th className="px-4 py-3">{t('campaign')}</th>
                        <th className="px-4 py-3 text-center">{t('prequalifications')}</th>
                        <th className="px-4 py-3 text-center">Awarded</th>
                        <th className="px-4 py-3 text-center">{t('convRateAbbr')}</th>
                        <th className="px-4 py-3 text-right">Tender Value</th>
                        <th className="px-4 py-3 text-right">Avg Tender</th>
                        <th className="px-4 py-3 w-10"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {campaignStats.slice(0, visibleCampaigns).map((campaign) => (
                        <React.Fragment key={campaign.name}>
                          <tr
                            className="hover:bg-slate-50 cursor-pointer transition-colors"
                            onClick={() => {
                              setSelectedCampaign(campaign.name);
                              setActiveTab('prequalifications');
                              setVisibleLeads(50);
                            }}
                          >
                            <td className="px-4 py-3">
                              <div className="font-medium text-gray-900">{campaign.name}</div>
                              <div className="text-xs text-gray-400 mt-0.5">
                                {campaign.platforms.join(', ')}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-center font-medium">{campaign.count}</td>
                            <td className="px-4 py-3 text-center">
                              <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                                {campaign.booked}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className={`font-medium ${campaign.conversionRate >= 5 ? 'text-green-600' :
                                campaign.conversionRate >= 2 ? 'text-yellow-600' : 'text-red-500'
                                }`}>
                                {formatPercent(campaign.conversionRate)}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right font-medium text-gray-900">
                              {formatCurrency(campaign.totalRevenue)}
                            </td>
                            <td className="px-4 py-3 text-right text-gray-600">
                              {formatCurrency(campaign.avgDealSize)}
                            </td>
                            <td className="px-4 py-3">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleRowExpand(campaign.name);
                                }}
                                className="p-1 hover:bg-gray-100 rounded"
                              >
                                {expandedRows.has(campaign.name) ?
                                  <ChevronUp className="w-4 h-4 text-gray-400" /> :
                                  <ChevronDown className="w-4 h-4 text-gray-400" />
                                }
                              </button>
                            </td>
                          </tr>
                          {expandedRows.has(campaign.name) && (
                            <tr className="bg-slate-50">
                              <td colSpan={7} className="px-4 py-3">
                                <div className="grid grid-cols-2 gap-4 text-sm">
                                  <div>
                                    <span className="text-gray-500">Ad Sets:</span>
                                    <div className="flex flex-wrap gap-1 mt-1">
                                      {campaign.adSets.length > 0 ? campaign.adSets.map(adSet => (
                                        <span key={adSet} className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">
                                          {adSet}
                                        </span>
                                      )) : <span className="text-gray-400 text-xs">No data</span>}
                                    </div>
                                  </div>
                                  <div>
                                    <span className="text-gray-500">Ads:</span>
                                    <div className="flex flex-wrap gap-1 mt-1">
                                      {campaign.ads.length > 0 ? campaign.ads.map(ad => (
                                        <span key={ad} className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs">
                                          {ad}
                                        </span>
                                      )) : <span className="text-gray-400 text-xs">No data</span>}
                                    </div>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                  {campaignStats.length > visibleCampaigns && (
                    <div className="p-4 flex justify-center bg-slate-50 border-t">
                      <button
                        onClick={() => setVisibleCampaigns(prev => prev + 50)}
                        className="px-4 py-2 bg-white border border-gray-200 text-blue-600 hover:bg-blue-50 rounded-lg text-sm font-medium transition-colors shadow-sm"
                      >
                        {t('loadMore')} ({campaignStats.length - visibleCampaigns} {t('remaining').toLowerCase()})
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Locations Tab */}
          {(activeTab === 'locations' && isMetaSource) && (
            <div className="space-y-3">
              <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-3">
                Location Breakdown
              </h3>

              {locationStats.filter(l => l.name !== 'Unknown').length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <MapPin className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No location data available</p>
                  <p className="text-xs mt-1">Configure city field in your Meta prequalification form</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {locationStats.slice(0, visibleLocations).map((location) => (
                    <div
                      key={location.name}
                      className="bg-white p-4 rounded-xl border hover:shadow-md transition-shadow cursor-pointer"
                      onClick={() => {
                        // Could filter by location
                      }}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <MapPin className="w-4 h-4 text-gray-400" />
                        <span className="font-medium text-gray-900">{location.name}</span>
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-center">
                        <div>
                          <div className="text-lg font-bold text-gray-900">{location.count}</div>
                          <div className="text-xs text-gray-500">Prequalifications</div>
                        </div>
                        <div>
                          <div className="text-lg font-bold text-green-600">{location.booked}</div>
                          <div className="text-xs text-gray-500">Booked</div>
                        </div>
                        <div>
                          <div className={`text-lg font-bold ${location.conversionRate >= 5 ? 'text-green-600' :
                            location.conversionRate >= 2 ? 'text-yellow-600' : 'text-red-500'
                            }`}>
                            {formatPercent(location.conversionRate)}
                          </div>
                          <div className="text-xs text-gray-500">Conv</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {locationStats.filter(l => l.name !== 'Unknown').length > visibleLocations && (
                <div className="mt-4 flex justify-center">
                  <button
                    onClick={() => setVisibleLocations(prev => prev + 50)}
                    className="px-4 py-2 bg-white border border-gray-200 text-blue-600 hover:bg-blue-50 rounded-lg text-sm font-medium transition-colors shadow-sm"
                  >
                    {t('loadMore')} ({locationStats.filter(l => l.name !== 'Unknown').length - visibleLocations} {t('remaining').toLowerCase()})
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Prequalifications List Tab (or default for non-Meta sources) */}
          {(activeTab === 'prequalifications' || !isMetaSource) && (
            <div className="space-y-3">
              {/* Filter Bar */}
              <div className="flex gap-3 items-center">
                {isMetaSource && selectedCampaign && (
                  <div className="flex items-center gap-2 bg-blue-50 text-blue-700 px-3 py-1.5 rounded-lg text-sm">
                    <Filter className="w-4 h-4" />
                    {selectedCampaign}
                    <button
                      onClick={() => setSelectedCampaign(null)}
                      className="ml-1 hover:bg-blue-100 rounded p-0.5"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                )}
                <Dropdown className="min-w-[150px]">
                  <DropdownTrigger>
                    <div className="flex items-center justify-between px-3 py-2 border rounded-lg text-sm bg-white outline-none min-w-[150px]">
                      {typeFilter === 'All' ? 'All Types' : typeFilter === 'Prequalification' ? 'Prequalifications' : 'Tenders & Proposals'}
                      <ChevronDown className="w-4 h-4 ml-2 text-gray-500" />
                    </div>
                  </DropdownTrigger>
                  <DropdownPopover placement="bottom">
                    <DropdownMenu
                      selectionMode="single"
                      selectedKeys={new Set([typeFilter])}
                      onSelectionChange={(keys) => {
                        const arr = Array.from(keys);
                        if (arr.length > 0) setTypeFilter(arr[0]);
                      }}
                    >
                      <DropdownItem id="Prequalification">Prequalifications</DropdownItem>
                      <DropdownItem id="Tender/Proposal">Tenders & Proposals</DropdownItem>
                    </DropdownMenu>
                  </DropdownPopover>
                </Dropdown>

                <div className="flex-1 relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search Records..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm"
                  />
                </div>
                <div className="text-sm text-gray-500 whitespace-nowrap">
                  {filteredLeads.length} {t('records')}
                </div>
              </div>

              {/* Prequalifications Grid */}
              <div className="space-y-2">
                {filteredLeads.length === 0 ? (
                  <div className="text-center py-12 text-gray-400">
                    <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>No records found</p>
                  </div>
                ) : (
                  filteredLeads.slice(0, visibleLeads).map((prequalification) => (
                    <div
                      key={prequalification.id}
                      onClick={() => onSelectLead(prequalification)}
                      className="bg-white p-4 rounded-xl border hover:shadow-md hover:border-blue-300 transition-all cursor-pointer"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-900 truncate">{prequalification.clientName}</span>
                            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-md ${prequalification.itemType === 'Prequalification' ? 'bg-blue-100 text-blue-700' : 'bg-emerald-100 text-emerald-700'}`}>
                              {prequalification.itemType}
                            </span>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${prequalification.stage === 'Booked' ? 'bg-green-100 text-green-700' :
                              prequalification.stage === 'Lost' ? 'bg-red-100 text-red-700' :
                                'bg-gray-100 text-gray-600'
                              }`}>
                              {prequalification.stage}
                            </span>
                          </div>
                          <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
                            {prequalification.phone && (
                              <span className="flex items-center gap-1">
                                <Phone className="w-3 h-3" /> {prequalification.phone}
                              </span>
                            )}
                            {prequalification.email && (
                              <span className="flex items-center gap-1">
                                <Mail className="w-3 h-3" /> {prequalification.email}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Custom Field Chips */}
                        <div className="flex items-center gap-2 ml-4">
                          {prequalification.projectName && (
                            <span className="px-2 py-1 bg-indigo-50 text-indigo-700 rounded text-xs font-medium truncate max-w-[150px]">
                              {prequalification.projectName}
                            </span>
                          )}
                          {prequalification.budgetRange && (
                            <span className="px-2 py-1 bg-emerald-50 text-emerald-700 rounded text-xs font-medium">
                              {prequalification.budgetRange}
                            </span>
                          )}
                          {prequalification.eventDate && (
                            <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs font-medium flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {formatDate(prequalification.eventDate, { month: 'short', year: 'numeric' })}
                            </span>
                          )}
                          {prequalification.guests && (
                            <span className="px-2 py-1 bg-purple-50 text-purple-700 rounded text-xs font-medium flex items-center gap-1">
                              <Users className="w-3 h-3" />
                              {prequalification.guests}
                            </span>
                          )}
                          {prequalification.amount > 0 && (
                            <span className="px-2 py-1 bg-amber-50 text-amber-700 rounded text-xs font-medium">
                              {formatCurrency(prequalification.amount)}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Meta Campaign Info */}
                      {isMetaSource && prequalification.campaignName && (
                        <div className="mt-2 pt-2 border-t flex items-center gap-2 text-xs text-gray-400">
                          <span className="bg-gray-100 px-2 py-0.5 rounded">{prequalification.campaignName}</span>
                          {prequalification.platform && (
                            <span className="bg-gray-100 px-2 py-0.5 rounded capitalize">{prequalification.platform}</span>
                          )}
                          {prequalification.adName && (
                            <span className="bg-gray-100 px-2 py-0.5 rounded truncate max-w-[200px]">{prequalification.adName}</span>
                          )}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>

              {filteredLeads.length > visibleLeads && (
                <div className="mt-4 flex justify-center pb-4">
                  <button
                    onClick={() => setVisibleLeads(prev => prev + 50)}
                    className="px-4 py-2 bg-white border border-gray-200 text-blue-600 hover:bg-blue-50 rounded-lg text-sm font-medium transition-colors shadow-sm"
                  >
                    {t('loadMore')} ({filteredLeads.length - visibleLeads} {t('remaining').toLowerCase()})
                  </button>
                </div>
              )}
            </div>
          )}
          </>
          )}
        </div>
      </div>
    </div>
  );
}
